/**
 * Corpse / loot-drop render manager for state.corpses.
 *
 * Two visuals, keyed off `Corpse.isDrop`:
 *  - DEATH corpse (isDrop=false): a findable beacon — gold ground glow + outline
 *    ring + a faint vertical light beam + a bobbing glow sprite. A death corpse is
 *    a bag of loot + gold, so the beacon reads from across the map.
 *  - HAND-DROPPED item (isDrop=true): a small 3D PROP of the item resting on the
 *    ground (a tinted nature mesh, the same look creature kills drop — meat is a
 *    red mushroom, leather a brown pebble, etc.; gear with no model gets a generic
 *    per-slot prop). A deliberate drop looks like a native 3D item lying there,
 *    not a death pile (Aviv 2026-06-26).
 *
 * Pickup is position-based (right-click loot + corpse-loot proximity), so these
 * visuals are purely cosmetic. reconcile() is called once per frame from main.ts.
 */
import * as THREE from "three";
import type { Corpse } from "@tribez/shared";
import type { AssetBundle, DropPropKey, NatureKey } from "../assets";
import { toToon } from "../toon";
import { dropMeshSpec } from "./drop-meshes";

export interface CorpseRenderManager {
  group: THREE.Group;
  reconcile(entries: Iterable<[string, Corpse]>): void;
  dispose(): void;
}

const DISC_RADIUS = 0.95;   // ground glow (up from 0.5 — easy to spot)
const BEAM_HEIGHT = 3.0;    // vertical light shaft, findable from a distance
const FLOAT_Y = 1.6;        // beacon sprite hover height above the drop
const SPRITE_BASE = 1.5;    // beacon sprite world size before pulse
const GOLD = 0xffd24a;
const DROP_BOB = 0.06;      // hand-drop prop hover amplitude (no pulse)
const DROP_SIZE_MULT = 1.35; // global scale on every drop prop's target height (tune-by-eye knob)

/** One-time radial-gradient glow texture for the floating beacon sprite. */
function makeGlowTexture(): THREE.Texture {
  const size = 64;
  const cv = document.createElement("canvas");
  cv.width = cv.height = size;
  const g = cv.getContext("2d")!;
  const grad = g.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  grad.addColorStop(0, "rgba(255,255,255,1)");
  grad.addColorStop(0.4, "rgba(255,220,120,0.8)");
  grad.addColorStop(1, "rgba(255,200,80,0)");
  g.fillStyle = grad;
  g.fillRect(0, 0, size, size);
  const tex = new THREE.CanvasTexture(cv);
  tex.needsUpdate = true;
  return tex;
}

interface Entry {
  obj: THREE.Group;
  isDrop: boolean;
  sprite?: THREE.Sprite;     // beacon only
  bobNode?: THREE.Object3D;  // drop prop — bobs (no pulse)
}

export function createCorpseRender(
  scene: THREE.Scene,
  sampleHeight: (x: number, z: number) => number,
  assets: AssetBundle,
): CorpseRenderManager {
  const group = new THREE.Group();
  group.name = "corpses";
  scene.add(group);

  // --- shared BEACON resources (one of each, reused across every death corpse) ---
  const discGeom = new THREE.CircleGeometry(DISC_RADIUS, 28);
  discGeom.rotateX(-Math.PI / 2);
  const discMat = new THREE.MeshBasicMaterial({
    color: GOLD, transparent: true, opacity: 0.4,
    depthWrite: false, blending: THREE.AdditiveBlending,
  });

  const ringGeom = new THREE.RingGeometry(DISC_RADIUS * 0.82, DISC_RADIUS, 32);
  ringGeom.rotateX(-Math.PI / 2);
  const ringMat = new THREE.MeshBasicMaterial({
    color: GOLD, transparent: true, opacity: 0.85,
    depthWrite: false, blending: THREE.AdditiveBlending,
  });

  const beamGeom = new THREE.CylinderGeometry(0.12, 0.12, BEAM_HEIGHT, 8, 1, true);
  beamGeom.translate(0, BEAM_HEIGHT / 2, 0);
  const beamMat = new THREE.MeshBasicMaterial({
    color: GOLD, transparent: true, opacity: 0.16,
    depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide,
  });

  const glowTex = makeGlowTexture();
  const spriteMat = new THREE.SpriteMaterial({
    map: glowTex, color: GOLD, transparent: true, opacity: 0.9,
    depthWrite: false, blending: THREE.AdditiveBlending,
  });

  // --- HAND-DROP resources: a shared soft contact shadow under the prop ---
  const shadowGeom = new THREE.CircleGeometry(0.42, 20);
  shadowGeom.rotateX(-Math.PI / 2);
  const shadowMat = new THREE.MeshBasicMaterial({
    color: 0x000000, transparent: true, opacity: 0.26, depthWrite: false,
  });

  function firstItemId(c: Corpse): string {
    let id = "";
    c.items.forEach((s) => { if (!id && s.itemId !== "" && s.count > 0) id = s.itemId; });
    return id;
  }

  // Clone a small tinted nature prop for the dropped item. Geometry is shared with
  // the source GLTF (read-only); the material is a fresh toon clone, so other users
  // of that mesh are unaffected. The prop is grounded so its base sits at y=0.
  function buildDropProp(itemId: string): THREE.Object3D {
    const root = new THREE.Group();
    const spec = dropMeshSpec(itemId);
    // EXPLORER FBX props live in dropProps; materials/foods use nature GLTFs.
    const source = spec.source === "prop"
      ? assets.dropProps[spec.key as DropPropKey]
      : assets.nature[spec.key as NatureKey]?.scene;
    if (!source) return root;
    const inner = source.clone(true);
    inner.traverse((n) => {
      const m = n as THREE.Mesh;
      if (!m.isMesh) return;
      const src = Array.isArray(m.material) ? m.material[0]! : m.material;
      const toon = toToon(src, { tintColor: spec.tint, noiseScale: 2.5 });
      if (spec.source === "prop") { toon.map = null; toon.needsUpdate = true; } // FBX props have no usable texture
      if (spec.emissive !== undefined) {
        toon.emissive = new THREE.Color(spec.emissive);
        toon.emissiveIntensity = 0.5;
      }
      m.material = toon;
      m.castShadow = false;
      m.receiveShadow = false;
    });
    // Scale so the prop's LARGEST native dimension lands at the spec's target
    // size, so props of very different native shapes all read at a consistent
    // ground footprint. (Normalizing by height alone blew up flat props — a
    // pebble is ~0.49 wide × 0.10 tall, so height-fit made leather a giant
    // pancake; playtest 2026-06-27, Aviv.)
    const native = new THREE.Box3().setFromObject(inner);
    const nativeMax = Math.max(
      native.max.x - native.min.x,
      native.max.y - native.min.y,
      native.max.z - native.min.z,
    ) || 1;
    inner.scale.setScalar((spec.height * DROP_SIZE_MULT) / nativeMax);
    // Sit the base on the ground regardless of the source mesh's pivot.
    const box = new THREE.Box3().setFromObject(inner);
    inner.position.y -= box.min.y;
    root.add(inner);
    return root;
  }

  const entries = new Map<string, Entry>();

  function addBeacon(c: Corpse): void {
    const g = new THREE.Group();
    g.position.set(c.x, sampleHeight(c.x, c.z) + 0.02, c.z);

    const disc = new THREE.Mesh(discGeom, discMat);
    const ring = new THREE.Mesh(ringGeom, ringMat);
    ring.position.y = 0.01;
    const beam = new THREE.Mesh(beamGeom, beamMat);
    const sprite = new THREE.Sprite(spriteMat);
    sprite.scale.setScalar(SPRITE_BASE);
    sprite.position.y = FLOAT_Y;

    g.add(disc, ring, beam, sprite);
    g.userData = { id: c.id };
    group.add(g);
    entries.set(c.id, { obj: g, isDrop: false, sprite });
  }

  function addDrop(c: Corpse): void {
    const g = new THREE.Group();
    g.position.set(c.x, sampleHeight(c.x, c.z) + 0.02, c.z);

    const shadow = new THREE.Mesh(shadowGeom, shadowMat);
    shadow.position.y = 0.01;
    const prop = buildDropProp(firstItemId(c));

    g.add(shadow, prop);
    g.userData = { id: c.id };
    group.add(g);
    entries.set(c.id, { obj: g, isDrop: true, bobNode: prop });
  }

  function add(c: Corpse): void {
    if (c.isDrop) addDrop(c); else addBeacon(c);
  }

  function remove(id: string): void {
    const e = entries.get(id);
    if (!e) return;
    if (e.isDrop && e.bobNode) {
      // Dispose the per-drop cloned toon materials (geometry is shared — leave it).
      e.bobNode.traverse((n) => {
        const m = n as THREE.Mesh;
        if (!m.isMesh) return;
        const mats = Array.isArray(m.material) ? m.material : [m.material];
        for (const mat of mats) mat?.dispose();
      });
    }
    group.remove(e.obj);
    entries.delete(id);
    // Shared beacon geometry/materials are never disposed here.
  }

  function reconcile(list: Iterable<[string, Corpse]>): void {
    const now = performance.now();
    const bob = Math.sin(now / 500) * 0.12;          // beacon hover
    const pulse = 0.85 + 0.15 * Math.sin(now / 350); // beacon throb
    const dropBob = Math.sin(now / 650) * DROP_BOB;  // gentle prop hover (no pulse)
    const seen = new Set<string>();
    for (const [id, c] of list) {
      seen.add(id);
      let e = entries.get(id);
      if (!e) { add(c); e = entries.get(id)!; }
      e.obj.position.x = c.x;
      e.obj.position.z = c.z;
      e.obj.position.y = sampleHeight(c.x, c.z) + 0.02;
      if (e.isDrop) {
        if (e.bobNode) e.bobNode.position.y = dropBob;
      } else if (e.sprite) {
        e.sprite.position.y = FLOAT_Y + bob;
        e.sprite.scale.setScalar(SPRITE_BASE * pulse);
      }
    }
    for (const id of entries.keys()) {
      if (!seen.has(id)) remove(id);
    }
  }

  function dispose(): void {
    for (const id of Array.from(entries.keys())) remove(id);
    discGeom.dispose(); discMat.dispose();
    ringGeom.dispose(); ringMat.dispose();
    beamGeom.dispose(); beamMat.dispose();
    glowTex.dispose(); spriteMat.dispose();
    shadowGeom.dispose(); shadowMat.dispose();
    scene.remove(group);
  }

  return { group, reconcile, dispose };
}
